import React, { useState } from 'react';

const AdminSidebar = ({ activeTab, setActiveTab }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const menuItems = [
    {
      id: 'overview',
      label: 'Overview',
      icon: 'fas fa-chart-line',
      description: 'Dashboard overview and key metrics'
    },
    {
      id: 'users',
      label: 'User Management',
      icon: 'fas fa-users',
      description: 'Manage users and their credits'
    },
    {
      id: 'listings',
      label: 'Listings',
      icon: 'fas fa-list',
      description: 'Manage all platform listings'
    },
    {
      id: 'payments',
      label: 'Payments',
      icon: 'fas fa-credit-card',
      description: 'Payment history and transactions'
    },
    {
      id: 'rentals',
      label: 'Rental Activity',
      icon: 'fas fa-exchange-alt',
      description: 'Track rental transactions'
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: 'fas fa-chart-bar',
      description: 'Platform analytics and insights'
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: 'fas fa-cog',
      description: 'Platform configuration'
    }
  ];

  return (
    <>
      {/* Mobile Menu Button */}
      <div className="lg:hidden fixed top-4 right-4 z-50">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-3 mt-14 rounded-2xl shadow-lg text-white"
          style={{ backgroundColor: '#222831' }}
        >
          <i className={`fas ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'} text-lg`}></i>
        </button>
      </div>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      <div className={`
        fixed lg:relative top-0 left-0 h-screen z-40 transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        w-72 sm:w-80 lg:w-56 xl:w-64 overflow-y-auto flex-shrink-0 admin-sidebar-scroll
      `}
      style={{ backgroundColor: '#222831' }}
      >
        <div
          className="h-full min-h-screen p-4 lg:p-5 border-r-2 lg:border-r-2"
          style={{ backgroundColor: '#222831', borderColor: '#393E46' }}
        >
          {/* Enhanced Header */}
          <div className="mb-5 lg:mb-6">
            <div className="flex items-center space-x-3 mb-3 lg:mb-4">
              <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#948979' }}>
                <i className="fas fa-crown text-white text-base lg:text-lg"></i>
              </div>
              <div>
                <h2 className="text-base lg:text-xl font-bold text-white">
                  Admin Panel
                </h2>
                <p className="text-xs font-medium" style={{ color: '#DFD0B8' }}>VestrioZ Management</p>
              </div>
            </div>

            {/* Enhanced Admin Badge */}
            <div className="rounded-2xl p-3 lg:p-4 border-2 shadow-lg" style={{ backgroundColor: '#393E46', borderColor: '#948979' }}>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-xl flex items-center justify-center shadow-md" style={{ backgroundColor: '#948979' }}>
                  <i className="fas fa-shield-alt text-white text-sm lg:text-base"></i>
                </div>
                <div>
                  <h3 className="font-bold text-sm lg:text-base text-white">Administrator</h3>
                  <p className="text-xs font-medium" style={{ color: '#DFD0B8' }}>Full Platform Access</p>
                </div>
              </div>
              <div className="mt-2 lg:mt-3 pt-2 lg:pt-3 border-t" style={{ borderColor: '#948979' }}>
                <div className="flex items-center justify-between text-xs">
                  <span style={{ color: '#DFD0B8' }}>Status:</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#948979' }}></div>
                    <span className="font-semibold text-white">Online</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Navigation */}
          <nav className="space-y-2 mb-5 lg:mb-6">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveTab(item.id);
                  setIsMobileMenuOpen(false); // Close mobile menu on selection
                }}
                className={`w-full text-left p-3 rounded-2xl transition-all duration-300 group relative overflow-hidden border-2 ${activeTab === item.id
                    ? 'text-white shadow-xl transform scale-105 border-transparent'
                    : 'hover:shadow-lg hover:scale-102 border-transparent hover:border-gray-100'
                  }`}
                style={activeTab === item.id ?
                  { backgroundColor: '#948979' } :
                  { backgroundColor: 'transparent', color: '#DFD0B8' }
                }
              >
                {/* Active state background */}
                {activeTab === item.id && (
                  <div
                    className="absolute inset-0 rounded-2xl opacity-90"
                    style={{ backgroundColor: '#948979' }}
                  ></div>
                )}

                {/* Hover background */}
                {activeTab !== item.id && (
                  <div
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ backgroundColor: '#393E46' }}
                  ></div>
                )}

                <div className="relative flex items-center space-x-3">
                  <div className={`w-8 h-8 lg:w-9 lg:h-9 rounded-xl flex items-center justify-center transition-all duration-300 shadow-md ${activeTab === item.id
                      ? 'bg-white/20 text-white'
                      : 'text-white group-hover:scale-110'
                    }`}
                    style={activeTab !== item.id ? { backgroundColor: '#393E46' } : {}}
                  >
                    <i className={`${item.icon} text-sm`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`font-bold text-sm transition-colors ${activeTab === item.id ? 'text-white' : 'group-hover:text-white'
                      }`}
                      style={activeTab !== item.id ? { color: '#DFD0B8' } : {}}
                    >
                      {item.label}
                    </div>
                  </div>
                  {activeTab === item.id && (
                    <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#DFD0B8' }}></div>
                  )}
                </div>
              </button>
            ))}
          </nav>

          {/* Enhanced Bottom Section */}
          <div className="pt-4 lg:pt-5 border-t" style={{ borderColor: '#393E46' }}>
            <div className="rounded-2xl p-3 lg:p-4 border-2 shadow-lg" style={{ backgroundColor: '#393E46', borderColor: '#948979' }}>
              <div className="text-center">
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-2xl flex items-center justify-center mx-auto mb-2 lg:mb-3 shadow-lg" style={{ backgroundColor: '#948979' }}>
                  <i className="fas fa-heartbeat text-white text-base lg:text-lg"></i>
                </div>
                <h3 className="font-bold text-sm lg:text-base text-white mb-2">Platform Health</h3>
                <div className="flex items-center justify-center space-x-2 mb-2 lg:mb-3">
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#948979' }}></div>
                  <span className="text-xs lg:text-sm font-semibold" style={{ color: '#DFD0B8' }}>All Systems Operational</span>
                </div>
                <p className="text-xs leading-relaxed mb-3" style={{ color: '#DFD0B8' }}>
                  Your VestrioZ platform is running smoothly with all services online.
                </p>

                {/* System Status Indicators */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-center p-2 rounded-xl" style={{ backgroundColor: '#222831' }}>
                    <i className="fas fa-server text-white text-sm mb-1"></i>
                    <div className="text-xs font-medium text-white">Server</div>
                    <div className="text-xs" style={{ color: '#948979' }}>Online</div>
                  </div>
                  <div className="text-center p-2 rounded-xl" style={{ backgroundColor: '#222831' }}>
                    <i className="fas fa-database text-white text-sm mb-1"></i>
                    <div className="text-xs font-medium text-white">Database</div>
                    <div className="text-xs" style={{ color: '#948979' }}>Connected</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminSidebar;